import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';

import { orderAPI } from '../api/orderAPI';
import { activeOrdersCountQueryKey } from './getActiveOrdersCount';
import { custonSooner } from '../../../components/CustopmSooner';
import { captureApiError } from '../../../lib/sentry-utils';

/**
 * Отмена заказа пользователем — пока он не оплачен и статус это допускает.
 *
 * Бэкенд отвечает 200 и `success: false`, если отменять уже поздно (заказ
 * взяли в работу между открытием карточки и подтверждением) — это не сбой,
 * а обычный отказ, его текст показываем как есть.
 */
export const useCancelOrder = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (orderId: number) => {
      const res = await orderAPI.cancelOrder(orderId);

      if (!res.data.success) {
        throw new Error(res.data.message ?? res.data.title ?? t('somethingWentWrong'));
      }

      return res.data;
    },
    onSuccess: async (_data, orderId) => {
      custonSooner.success(t('order-cancelled'));

      // Карточка — чтобы статус и кнопка обновились сразу, список и счётчик
      // активных — чтобы заказ ушёл из «в процессе» ещё до возврата назад.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['order', String(orderId)] }),
        queryClient.invalidateQueries({ queryKey: ['orders'] }),
        queryClient.invalidateQueries({ queryKey: activeOrdersCountQueryKey }),
      ]);
    },
    onError: error => {
      captureApiError(error, '/order/cancel', 'POST');
      custonSooner.error(
        error instanceof Error ? error.message : t('somethingWentWrong')
      );
    },
  });

  return {
    cancelOrder: mutateAsync,
    isPending,
  };
};
